
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountdownUnit from './CountdownUnit';
import styles from './DesktopPromotionBanner.module.css';
import Image from 'next/image';
import { useLayout } from '@/contexts/layout';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getStrapiURL } from '@/api/api';
import { usePromotionBannerLogic } from '@/hooks/usePromotionBannerLogic';

const parseOfferText = (text: string, highlightClassName: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <span key={index} className={highlightClassName}>{part.slice(2, -2)}</span>;
    }
    return part;
  });
};

export default function DesktopPromotionBanner() {
  const { isVisible, timeLeft, handleClose, handleCopyCoupon, promotionBannerData } = usePromotionBannerLogic();
  const [showRedBanner, setShowRedBanner] = useState(false);
  const isLargeScreen = useMediaQuery(1440);

  useEffect(() => {
    if (!isVisible || !isLargeScreen) {
      setShowRedBanner(false);
      return;
    }
    const redBannerTimer = setTimeout(() => {
      setShowRedBanner(true);
    }, 2000);
    return () => clearTimeout(redBannerTimer);
  }, [isVisible, isLargeScreen]);

  const formatDigit = (num: number) => num.toString().padStart(2, '0');

  const offerTextParts = useMemo(() => {
    if (promotionBannerData?.offer_text) {
      const [line1, line2] = promotionBannerData.offer_text.split('<br>');
      return {
        line1: parseOfferText(line1 || '', styles.offerHighlight),
        line2: line2 ? parseOfferText(line2, styles.offerHighlight) : null
      };
    }
    return { line1: '', line2: null };
  }, [promotionBannerData?.offer_text]);

  return (
    <AnimatePresence>
      {isVisible && promotionBannerData && (
        <motion.div
          className={styles.bannerContainer}
          style={{ backgroundColor: promotionBannerData.main_background_color }}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          <button onClick={handleClose} className={styles.closeButton} aria-label="Fechar banner">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M7.15385 0.923105L1.00002 7.07693M1 0.92308L7.15385 7.07693" stroke="#939598" strokeWidth="0.984615" strokeLinecap="round" />
            </svg>
          </button>
          <div className={styles.contentWrapper}>
            <div className={styles.mainContent}>
              <div className={`${styles.backgroundCircle} ${styles.circle1}`}>
                <Image src="/icons/promotion-banner-circle-fill.svg" alt="" layout="fill" />
              </div>
              <div className={`${styles.backgroundCircle} ${styles.circle2}`}>
                <Image src="/icons/promotion-banner-circle-stroke.svg" alt="" layout="fill" />
              </div>
              <div className={`${styles.textAndCouponContainer} ${showRedBanner ? styles.shifted : ''}`}>
                <div className={styles.couponSection}>
                  <div className={styles.couponLabelContainer}>
                    {promotionBannerData.coupon_label.split('<br>').map((line, index) => (
                      <p key={index} className={styles.couponLabelLine} style={{ color: promotionBannerData.text_color }}>{line}</p>
                    ))}
                  </div>
                  <div className={styles.couponBox}>
                    <div className={styles.couponBackground}>
                      <Image src="/icons/coupon-background.svg" layout="fill" objectFit="fill" alt="Fundo do cupom" />
                    </div>
                    <span className={styles.couponCode} style={{ color: promotionBannerData.text_color }}>{promotionBannerData.coupon_code}</span>
                    <button className={styles.copyButton} onClick={handleCopyCoupon} aria-label="Copiar cupom"></button>
                  </div>
                </div>

                <div className={styles.offerTextContainer}>
                  <p className={styles.offerTextLine1} style={{ color: promotionBannerData.text_color }}>
                    {offerTextParts.line1}
                  </p>
                  {offerTextParts.line2 && (
                    <p className={styles.offerTextLine2} style={{ color: promotionBannerData.text_color }}>
                      {offerTextParts.line2}
                    </p>
                  )}
                </div>

                {(promotionBannerData.promotion_type === 'date_range' || promotionBannerData.countdown_duration_minutes > 0) && (
                  <div className={styles.countdownSection}>
                    <CountdownUnit
                      digit={formatDigit(timeLeft.days)}
                      label="dias"
                      styles={styles}
                      digitColor={promotionBannerData.countdown_digit_color}
                      labelColor={promotionBannerData.countdown_label_color}
                      borderColor={promotionBannerData.countdown_border_color}
                      backgroundColor={promotionBannerData.countdown_background_color}
                    />
                    <span className={styles.separator} style={{ color: promotionBannerData.countdown_separator_color }}>:</span>
                    <CountdownUnit
                      digit={formatDigit(timeLeft.hours)}
                      label="horas"
                      styles={styles}
                      digitColor={promotionBannerData.countdown_digit_color}
                      labelColor={promotionBannerData.countdown_label_color}
                      borderColor={promotionBannerData.countdown_border_color}
                      backgroundColor={promotionBannerData.countdown_background_color}
                    />
                    <span className={styles.separator} style={{ color: promotionBannerData.countdown_separator_color }}>:</span>
                    <CountdownUnit
                      digit={formatDigit(timeLeft.minutes)}
                      label="minutos"
                      styles={styles}
                      digitColor={promotionBannerData.countdown_digit_color}
                      labelColor={promotionBannerData.countdown_label_color}
                      borderColor={promotionBannerData.countdown_border_color}
                      backgroundColor={promotionBannerData.countdown_background_color}
                    />
                    <span className={styles.separator} style={{ color: promotionBannerData.countdown_separator_color }}>:</span>
                    <CountdownUnit
                      digit={formatDigit(timeLeft.seconds)}
                      label="segundos"
                      styles={styles}
                      digitColor={promotionBannerData.countdown_digit_color}
                      labelColor={promotionBannerData.countdown_label_color}
                      borderColor={promotionBannerData.countdown_border_color}
                      backgroundColor={promotionBannerData.countdown_background_color}
                    />
                  </div>
                )}
              </div>
            </div>

            <AnimatePresence>
              {showRedBanner && promotionBannerData.vehicle_image && (
                <motion.div
                  className={styles.redBanner}
                  style={{ backgroundColor: promotionBannerData.animated_banner_background_color }}
                  initial={{ width: 0, paddingLeft: 0, paddingRight: 0, opacity: 0 }}
                  animate={{ width: '35%', paddingLeft: '80px', paddingRight: '40px', opacity: 1 }}
                  exit={{ width: 0, paddingLeft: 0, paddingRight: 0, opacity: 0 }}
                  transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1], delay: 0.3 }}
                >
                  <div className={styles.carImageWrapper}>
                    <Image src={getStrapiURL(promotionBannerData.vehicle_image.url)} alt={promotionBannerData.vehicle_image.alternativeText || "Carro"} layout="fill" objectFit="contain" />
                  </div>
                  <span className={styles.redBannerText} style={{ color: promotionBannerData.animated_banner_text_color }}>{promotionBannerData.animated_banner_text}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
